import type { MockEdge, MockNode } from './types'
import type { KnowledgeGraphData } from './useKnowledgeGraphData'

/**
 * The graph Tree View draws (DSGN-63 Tree View frames), derived from the
 * page's graph; Radial View keeps the page's graph as it is.
 *
 * The agent graph is flat: tenant → every sheet → its equipment. Drawn as a
 * tree that is one row of a hundred sheets, where the design stacks
 * Enterprise → Operating Area → System → … down the page. Each sheet already
 * says which area of the plant and which system it belongs to, so here the
 * sheets are grouped under those two levels.
 *
 * And the tag-to-tag connectivity (thousands of lines on a real tenant) is
 * drawn once per pair of sheets it joins, labelled with how many connections
 * it stands for. A connection inside one sheet is inside that sheet's grid and
 * is not drawn. The details panel still lists every tag-level connection.
 *
 * A graph whose sheets carry no Area or System is returned unchanged.
 */
const MISSING = '—'

const attr = (n: MockNode, label: string): string | null => {
  const v = n.attributes?.find((a) => a.label === label)?.value?.trim()
  return v && v !== MISSING ? v : null
}

export const AREA_ID_PREFIX = 'tv-area:'
export const SYSTEM_ID_PREFIX = 'tv-system:'

export const treeViewGraph = (graph: KnowledgeGraphData): KnowledgeGraphData => {
  const byId = new Map(graph.nodes.map((n) => [n.id, n]))
  // Sheets to group: a node with an Area or System, under a parent that is drawn.
  const grouped = graph.nodes.filter((n) => n.parent && byId.has(n.parent) && (attr(n, 'Area') || attr(n, 'System')))
  if (grouped.length === 0) return graph

  const groupNodes = new Map<string, MockNode>()
  const counts = new Map<string, number>()
  const newParent = new Map<string, string>()
  const ensure = (id: string, label: string, type: MockNode['type'], parent: string) => {
    if (!groupNodes.has(id)) groupNodes.set(id, { id, label, type, parent })
    counts.set(id, (counts.get(id) ?? 0) + 1)
  }
  for (const n of grouped) {
    const top = n.parent as string
    const area = attr(n, 'Area')
    const system = attr(n, 'System')
    let parent = top
    if (area) {
      const id = `${AREA_ID_PREFIX}${top}|${area}`
      ensure(id, area, 'operating_area', top)
      parent = id
    }
    if (system) {
      const id = `${SYSTEM_ID_PREFIX}${top}|${area ?? ''}|${system}`
      ensure(id, system, 'system', parent)
      parent = id
    }
    newParent.set(n.id, parent)
  }

  const nodes: MockNode[] = [
    ...graph.nodes.map((n) => (newParent.has(n.id) ? { ...n, parent: newParent.get(n.id) } : n)),
    ...[...groupNodes.values()].map((g) => ({ ...g, attributes: [{ label: 'Sheets', value: String(counts.get(g.id) ?? 0) }] })),
  ]
  const parentOf = new Map(nodes.filter((n) => n.parent).map((n) => [n.id, n.parent as string]))

  // Each node's sheet: the node itself if it is at sheet level (a child of a
  // parent whose sheets were grouped, grouped or not), else that ancestor.
  const tops = new Set(grouped.map((n) => n.parent as string))
  const groupedIds = new Set(graph.nodes.filter((n) => n.parent && tops.has(n.parent)).map((n) => n.id))
  const anchorCache = new Map<string, string>()
  const anchor = (id: string): string => {
    const hit = anchorCache.get(id)
    if (hit) return hit
    let cur: string | undefined = id
    const seen = new Set<string>()
    while (cur && !groupedIds.has(cur) && !seen.has(cur)) {
      seen.add(cur)
      cur = parentOf.get(cur)
    }
    const a = cur && groupedIds.has(cur) ? cur : id
    anchorCache.set(id, a)
    return a
  }

  const edges: MockEdge[] = []
  const pairs = new Map<string, { source: string; target: string; n: number }>()
  for (const e of graph.edges) {
    if (parentOf.get(e.target) === e.source) {
      edges.push(e)
      continue
    }
    // The sheet's old parent edge: the sheet now hangs off its system.
    if (newParent.has(e.target) && byId.get(e.target)?.parent === e.source) continue
    const a = anchor(e.source)
    const b = anchor(e.target)
    if (a === e.source && b === e.target) {
      edges.push(e)
      continue
    }
    if (a === b) continue
    const key = a < b ? `${a}|${b}` : `${b}|${a}`
    const pair = pairs.get(key)
    if (pair) pair.n += 1
    else pairs.set(key, { source: a, target: b, n: 1 })
  }
  for (const g of groupNodes.values()) edges.push({ id: `tv-h:${g.id}`, source: g.parent as string, target: g.id })
  for (const n of grouped) edges.push({ id: `tv-h:${n.id}`, source: newParent.get(n.id) as string, target: n.id })
  pairs.forEach((p, key) => {
    edges.push({ id: `tv-x:${key}`, source: p.source, target: p.target, label: p.n === 1 ? '1 connection' : `${p.n} connections` })
  })
  return { nodes, edges }
}
